import {
  type CreateQuerySubStatusRequest,
  type QuerySubStatusDetail,
  QuerySubStatusDetailSchema,
  type QuerySubStatusListQuery,
  type QuerySubStatusListResponse,
  QuerySubStatusListResponseSchema,
  type QueryStatusOption,
  QueryStatusOptionListSchema,
  type UpdateQuerySubStatusRequest,
} from '@utazzo/shared';

import { apiClient } from '@/lib/api-client';

/**
 * Query Sub Status master API. Every response is validated with the shared
 * Zod schema so a server change can't sneak past the UI types.
 */

export const listQuerySubStatuses = async (
  query: QuerySubStatusListQuery,
): Promise<QuerySubStatusListResponse> => {
  const { data } = await apiClient.get<unknown>('/query-sub-statuses', { params: query });
  return QuerySubStatusListResponseSchema.parse(data);
};

export const getQuerySubStatus = async (id: string): Promise<QuerySubStatusDetail> => {
  const { data } = await apiClient.get<unknown>(`/query-sub-statuses/${id}`);
  return QuerySubStatusDetailSchema.parse(data);
};

export const createQuerySubStatus = async (
  payload: CreateQuerySubStatusRequest,
): Promise<QuerySubStatusDetail> => {
  const { data } = await apiClient.post<unknown>('/query-sub-statuses', payload);
  return QuerySubStatusDetailSchema.parse(data);
};

export const updateQuerySubStatus = async (
  id: string,
  payload: UpdateQuerySubStatusRequest,
): Promise<QuerySubStatusDetail> => {
  const { data } = await apiClient.patch<unknown>(`/query-sub-statuses/${id}`, payload);
  return QuerySubStatusDetailSchema.parse(data);
};

export const deleteQuerySubStatus = async (id: string): Promise<void> => {
  await apiClient.delete(`/query-sub-statuses/${id}`);
};

export const bulkDeleteQuerySubStatuses = async (
  ids: string[],
): Promise<{ updated: number; failed: { id: string; error: string }[] }> => {
  const { data } = await apiClient.post<{
    updated: number;
    failed: { id: string; error: string }[];
  }>('/query-sub-statuses/bulk-delete', { ids });
  return data;
};

export const bulkSetQuerySubStatusesStatus = async (
  ids: string[],
  isActive: boolean,
): Promise<{ updated: number; failed: { id: string; error: string }[] }> => {
  const { data } = await apiClient.post<{
    updated: number;
    failed: { id: string; error: string }[];
  }>('/query-sub-statuses/bulk-set-status', { ids, isActive });
  return data;
};

// ----- Parent status options -----

/** Slim { id, code, name } list from the query statuses options endpoint. */
export const listQueryStatusOptions = async (): Promise<QueryStatusOption[]> => {
  const { data } = await apiClient.get<unknown>('/query-statuses/options');
  return QueryStatusOptionListSchema.parse(data);
};
